import { useEffect, useState } from 'react';
import { SignedIn, SignedOut, useAuth, useUser } from '@clerk/clerk-react';
import { Route, Switch, Redirect, useLocation, useParams } from 'wouter';
import { setTokenProvider, trpc } from './lib/trpc';
import { useAsync } from './lib/useAsync';
import { localAuthEnabled, localEmailAtual, localIsLoggedIn } from './lib/localAuth';
import { pixelPageView, pixelLead } from './lib/pixel';
import { Shell } from './components/Shell';
import { ImportarPage } from './pages/Importar';
import { RiscoPage } from './pages/Risco';
import { ComunicacoesPage } from './pages/Comunicacoes';
import { ResultadoPage } from './pages/Resultado';
import { AjustesPage } from './pages/Ajustes';
import { ObrasPage } from './pages/Obras';
import { BoletosPage } from './pages/Boletos';
import { AppClientePage } from './pages/AppCliente';
import { TutoriaisPage } from './pages/Tutoriais';
import { IndiceIncc } from './pages/IndiceIncc';
import { PreviaCompradorPage } from './pages/PreviaComprador';
import { LoginPage } from './pages/Login';
import { LoginLocal } from './pages/LoginLocal';
import { VendasPage } from './pages/Vendas';
import { AdminApp } from './pages/admin/AdminApp';
import { PwaPrompts } from './components/pwa/PwaPrompts';
import { LoginComprador } from './pages/comprador/LoginComprador';
import { EntrarComprador } from './pages/comprador/EntrarComprador';
import { InicioComprador } from './pages/comprador/InicioComprador';
import { ParcelasComprador } from './pages/comprador/ParcelasComprador';
import { ObraComprador } from './pages/comprador/ObraComprador';
import { ConversaComprador } from './pages/comprador/ConversaComprador';
import { AprenderComprador } from './pages/comprador/AprenderComprador';
import { SenhaComprador } from './pages/comprador/SenhaComprador';
import { PerfilComprador } from './pages/comprador/PerfilComprador';
import { ArtigoComprador } from './pages/comprador/ArtigoComprador';
import { APP_BASE, rotaApp } from './pages/comprador/util';
import { sessaoCompradorAtual } from './lib/compradorAuth';
import { CompradorShell } from './components/comprador/CompradorShell';
import { marcarClienteB2B, ehClienteB2BConhecido } from './lib/mundoB2B';
import { destinoAnon, ehMundoComprador } from './lib/roteamento';
import { ehStandalone } from './lib/pwaInstall';

/** Tela de carregamento neutra (login em andamento, primeira busca de dados). */
export function CenteredLoading() {
  return <div className="flex min-h-screen items-center justify-center bg-bg text-ink-3">Carregando…</div>;
}

function TokenBridge() {
  const { getToken } = useAuth();
  useEffect(() => {
    setTokenProvider((opts) => getToken(opts));
  }, [getToken]);
  return null;
}

/**
 * PageView do Meta Pixel: dispara no primeiro mount (landing/entrar/criar-conta
 * ou já autenticado direto no app) e em toda troca de rota (todas as telas do
 * app, todo o console). Mora no topo de App() - roda em QUALQUER estado de auth.
 * A checagem de produção (!isProd) vive dentro de pixelPageView().
 */
function PixelPageViews() {
  const [location] = useLocation();
  useEffect(() => {
    pixelPageView();
  }, [location]);
  return null;
}

/**
 * Visitante DESLOGADO (ambiente A/B). Isolamento dos mundos:
 *  - "/entrar" → SEMPRE o login da incorporadora (ambiente B).
 *  - "/" → landing de vendas (ambiente A) SÓ para quem NÃO é usuário; um cliente
 *    já conhecido neste dispositivo é levado ao login (/entrar), nunca ao site de
 *    vendas ("quem já é cliente vai para /entrar").
 *  - qualquer outro path deslogado → login (nunca a landing).
 */
function AnonRouter() {
  const [location] = useLocation();
  const destino = destinoAnon(location, { clienteConhecido: ehClienteB2BConhecido(), standalone: ehStandalone() });
  if (destino === 'entrar') return <Redirect to="/entrar" replace />;
  if (destino === 'landing') return <VendasPage />;
  return <LoginPage />;
}

function LocalAnonRouter({ onSuccess }: { onSuccess: () => void }) {
  const [location] = useLocation();
  const destino = destinoAnon(location, { clienteConhecido: ehClienteB2BConhecido(), standalone: ehStandalone() });
  if (destino === 'entrar') return <Redirect to="/entrar" replace />;
  if (destino === 'landing') return <VendasPage />;
  return <LoginLocal onSuccess={onSuccess} />;
}

/** Marca o dispositivo como "cliente B2B" enquanto a sessão está ativa. */
function MarcaClienteB2B() {
  useEffect(() => {
    marcarClienteB2B();
  }, []);
  return null;
}

/**
 * Estado terminal do mundo do COMPRADOR (ambiente C): rota inexistente sob
 * /app/:slug/... ou /app sem slug. Fica DENTRO do casco do comprador — nunca
 * cai na landing (A) nem no login da incorporadora (B).
 */
function CompradorNaoEncontrado() {
  const [location] = useLocation();
  const slug = location.split('/')[2] ?? '';
  return (
    <CompradorShell>
      <div className="text-center" data-testid="comprador-nao-encontrado">
        <h1 className="text-[17px] font-semibold text-ink">Página não encontrada</h1>
        <p className="mt-2 text-sm text-ink-3">Este endereço não existe no aplicativo.</p>
        {slug && (
          <a href={rotaApp(slug)} className="mt-5 inline-block text-sm font-medium text-accent hover:underline">
            Voltar ao início
          </a>
        )}
      </div>
    </CompradorShell>
  );
}

/**
 * Base "/comprador/:slug" — decide entre a tela de login e o Início, olhando
 * se já existe uma sessão salva PARA ESTE slug (cada incorporadora tem sua
 * própria sessão no mesmo navegador).
 */
function EntradaComprador() {
  const { slug } = useParams<{ slug: string }>();
  const logado = Boolean(slug && sessaoCompradorAtual(slug));
  return logado ? <InicioComprador /> : <LoginComprador />;
}

/**
 * Rotas do app do COMPRADOR (Fase 7) — auth TOTALMENTE separada do operador
 * (sem Clerk, ver compradorAuth/*). Checada ANTES de qualquer coisa de Clerk
 * em App(): o comprador nunca deve cair no fluxo de login da incorporadora.
 * Prefixo público em APP_BASE (/app); o redirect do legado `/comprador/*` é
 * feito no guard de App() (não aqui), para não depender do matching de
 * wildcard multi-segmento do wouter.
 */
function CompradorRotas() {
  return (
    <Switch>
      <Route path={`${APP_BASE}/:slug/entrar`}>
        <EntrarComprador />
      </Route>
      <Route path={`${APP_BASE}/:slug/parcelas`}>
        <ParcelasComprador />
      </Route>
      <Route path={`${APP_BASE}/:slug/obra`}>
        <ObraComprador />
      </Route>
      <Route path={`${APP_BASE}/:slug/conversa`}>
        <ConversaComprador />
      </Route>
      <Route path={`${APP_BASE}/:slug/aprender/:artigoSlug`}>
        <ArtigoComprador />
      </Route>
      <Route path={`${APP_BASE}/:slug/aprender`}>
        <AprenderComprador />
      </Route>
      <Route path={`${APP_BASE}/:slug/senha`}>
        <SenhaComprador />
      </Route>
      <Route path={`${APP_BASE}/:slug/perfil`}>
        <PerfilComprador />
      </Route>
      <Route path={`${APP_BASE}/:slug`}>
        <EntradaComprador />
      </Route>
      {/* Qualquer outra rota do mundo do comprador (404 sob /app) fica NO
          mundo do comprador — nunca vaza para a landing nem para o login B2B. */}
      <Route>
        <CompradorNaoEncontrado />
      </Route>
    </Switch>
  );
}

/** Rotas do CLIENTE (incorporadora). */
function ClienteRotas() {
  return (
    <Switch>
      <Route path="/importar" component={ImportarPage} />
      <Route path="/resultado" component={ResultadoPage} />
      <Route path="/risco" component={RiscoPage} />
      <Route path="/comunicacoes" component={ComunicacoesPage} />
      <Route path="/tutoriais" component={TutoriaisPage} />
      <Route path="/indice-incc" component={IndiceIncc} />
      {/* App do Cliente: hub + sub-seções. Obras/Boletos são as MESMAS telas,
          só reposicionadas aqui dentro. */}
      <Route path="/app-cliente" component={AppClientePage} />
      <Route path="/app-cliente/obras" component={ObrasPage} />
      <Route path="/app-cliente/boletos" component={BoletosPage} />
      <Route path="/app-cliente/previa" component={PreviaCompradorPage} />
      {/* Rotas antigas: nunca dar 404 num link salvo — redireciona pro novo lugar. */}
      <Route path="/obras">
        <Redirect to="/app-cliente/obras" />
      </Route>
      <Route path="/boletos">
        <Redirect to="/app-cliente/boletos" />
      </Route>
      <Route path="/ajustes" component={AjustesPage} />
      <Route>
        <Redirect to="/importar" />
      </Route>
    </Switch>
  );
}

/**
 * Roteia dentro de UMA sidebar única (Shell):
 *  - Cliente comum: vê só a área do cliente; /admin/* redireciona para /importar.
 *  - Super-admin: vê a área do cliente + o bloco Console; navega em ambos.
 *
 * A incorporadora (org) é um detalhe interno 100% automático — o backend
 * deriva `orgId` direto do usuário logado (ver apps/api/src/context.ts), sem
 * nenhum passo de criação/seleção nem chamada de rede adicional. Por isso não
 * há gate de org aqui: assim que `me` carrega, a área do cliente já funciona.
 */
function AuthedRouter() {
  const { data: me, loading } = useAsync(() => trpc.me.query());
  const [location] = useLocation();

  if (loading) return <CenteredLoading />;
  const superAdmin = me?.superAdmin ?? false;
  const isAdminRoute = location.startsWith('/admin');

  // Cliente comum nunca alcança o Console.
  if (isAdminRoute && !superAdmin) return <Redirect to="/importar" />;
  // Super-admin cai no Console por padrão (mantém o comportamento pós-login).
  // "/entrar" e "/" logado nunca mostram login/landing — o logado já está dentro.
  if (superAdmin && (location === '/' || location === '' || location === '/entrar'))
    return <Redirect to="/admin/visao-geral" />;

  // Rotas do Console (só super-admin chega aqui).
  if (isAdminRoute) {
    return (
      <Shell superAdmin={superAdmin}>
        <AdminApp />
      </Shell>
    );
  }

  return <AreaCliente superAdmin={superAdmin} />;
}

/**
 * Área do cliente. O WhatsApp é coletado INLINE no formulário de signup (não há
 * mais gate pós-cadastro); aqui é só o app.
 */
function AreaCliente({ superAdmin }: { superAdmin: boolean }) {
  return (
    <Shell superAdmin={superAdmin}>
      <ClienteRotas />
    </Shell>
  );
}

/**
 * Grava, uma única vez após o signup do Clerk, o WhatsApp coletado no formulário
 * (viajou em `unsafeMetadata.whatsapp`) em `org_member_perfil`. Sem tela
 * intermediária: roda em background assim que a sessão fica ativa e limpa o
 * metadata. No modo local o WhatsApp é gravado direto no submit do LoginLocal.
 */
function PerfilFlush() {
  const { isLoaded, user } = useUser();
  useEffect(() => {
    if (!isLoaded || !user) return;
    const whatsapp = (user.unsafeMetadata?.whatsapp as string | undefined)?.trim();
    if (!whatsapp) return;
    trpc.perfil.salvar
      .mutate({ whatsapp })
      .then((r) => {
        if (r.leadPendente) pixelLead(user.id);
        return user.update({ unsafeMetadata: { ...user.unsafeMetadata, whatsapp: undefined } });
      })
      .catch(() => {});
  }, [isLoaded, user]);
  return null;
}

/**
 * Semeia o nome inicial da incorporadora (nome = e-mail) SÓ NA PRIMEIRA VEZ
 * (quando ainda não existe nenhum registro) — nunca sobrescreve um nome que o
 * usuário já tenha personalizado na sidebar (ver Shell.tsx/IncorporadoraBrand).
 * A org em si já existe e já funciona sem isto — `orgId` é derivado do
 * usuário no backend, sem passo de criação nenhum (ver AuthedRouter). Isto só
 * garante que a sidebar tenha um nome pra mostrar de cara; falha aqui é
 * silenciosa e não afeta upload nem navegação.
 */
function SincronizaIncorporadora() {
  const { isLoaded, user } = useUser();
  useEffect(() => {
    if (!isLoaded || !user) return;
    (async () => {
      const existente = await trpc.incorporadora.query().catch(() => null);
      if (existente) return; // já tem nome (default ou personalizado) — não mexe
      const nome = user.primaryEmailAddress?.emailAddress ?? user.fullName ?? 'Minha incorporadora';
      await trpc.syncIncorporadora.mutate({ nome }).catch(() => {});
    })();
  }, [isLoaded, user]);
  return null;
}

/**
 * Modo TEMPORÁRIO sem Clerk (VITE_AUTH_DISABLED=true): mantém a tela de login,
 * mas autentica localmente (VITE_ADMIN_EMAIL/PASSWORD). Precisa de
 * AUTH_DISABLED=true no backend para os dados carregarem. Remova as flags para
 * reativar o Clerk.
 */
function LocalApp() {
  const [logado, setLogado] = useState(localIsLoggedIn());
  useEffect(() => {
    if (!logado) return;
    marcarClienteB2B();
    (async () => {
      const existente = await trpc.incorporadora.query().catch(() => null);
      if (existente) return; // já tem nome (default ou personalizado) — não mexe
      const nome = localEmailAtual() || 'Minha incorporadora';
      await trpc.syncIncorporadora.mutate({ nome }).catch(() => {});
    })();
  }, [logado]);
  if (!logado) return <LocalAnonRouter onSuccess={() => setLogado(true)} />;
  // Sem TokenBridge: no modo local não há Clerk (backend em AUTH_DISABLED).
  return (
    <>
      <PwaPrompts />
      <AuthedRouter />
    </>
  );
}

export function App() {
  const [location] = useLocation();

  // Legado: qualquer `/comprador/<resto>` → `/app/<resto>` preservando o
  // subcaminho e a query (?token=...). Cobre os links de e-mail já enviados
  // quando a URL pública era `/comprador/:slug`. Feito aqui (não numa <Route>)
  // porque o wildcard multi-segmento do wouter não pega `/comprador/x/entrar`
  // de forma confiável. Pode sair quando não houver mais links antigos.
  if (location.startsWith('/comprador/')) {
    const destino = location.replace(/^\/comprador(?=\/|$)/, APP_BASE) + (window.location.search || '');
    return <Redirect to={destino} replace />;
  }

  // App do comprador: checado ANTES de Clerk/local-auth de propósito — é uma
  // identidade totalmente separada (nunca deve cair no login da incorporadora
  // nem depender de o operador estar logado).
  if (ehMundoComprador(location)) {
    return (
      <>
        <PixelPageViews />
        <CompradorRotas />
      </>
    );
  }

  if (localAuthEnabled()) {
    return (
      <>
        <PixelPageViews />
        <LocalApp />
      </>
    );
  }

  return (
    <>
      <PixelPageViews />
      {/* Convite de PWA fica SÓ na área logada (dentro de SignedIn/LocalApp),
          nunca na landing pública — visitante deslogado não recebe "instale o
          app". */}
      <SignedOut>
        <AnonRouter />
      </SignedOut>

      <SignedIn>
        <TokenBridge />
        <MarcaClienteB2B />
        <PerfilFlush />
        <SincronizaIncorporadora />
        <PwaPrompts />
        <AuthedRouter />
      </SignedIn>
    </>
  );
}
